import { ArrowRight, Check } from 'lucide-react'
import type { ReactNode } from 'react'
import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import FeatureFaq from '@/components/marketing/features/FeatureFaq'
import StatusBadge from './StatusBadge'
import TechBreadcrumbs from './TechBreadcrumbs'
import TechCta from './TechCta'
import TechSubnav from './TechSubnav'
import type { TechnologyAudienceProps, TechnologyModuleChip } from '@/types/technology'

/* The one template every audience page renders (plan 2026-10-18, audience-first):
 * distributors (/white-label), labels, catalogue owners (/managed-services) and
 * developers (/api).
 *
 * The owner's problem was "too many elements, scrambled", so the shape is fixed
 * and short: hero with ONE action (plus a quieter secondary one when the
 * registry gives it) → what you get (the page's single visual, then
 * 4–5 groups in a vertical sequence) → how it works → what's coming → FAQ → the
 * same action again. Text runs at most ~65ch wide.
 *
 * A capability is a CHIP linking to its card on a reference page, never a card
 * here. A chip is badged only when the module is not live: a badge on every
 * chip is a legend the reader has to learn, and "live" is the default anyway.
 *
 * `media` is the page's one visual, chosen by the page component so each page
 * bundles only its own widget. The catalogue-owners page passes none — its
 * "How it works" timeline is its visual, and a second steps graphic above the
 * groups would say the same thing twice.
 *
 * The FAQ is never inside a ScrollReveal: its answers must be visible text
 * without scrolling or JavaScript.
 */
export default function AudiencePage({
  hero,
  primary_cta,
  secondary_cta,
  answer,
  groups,
  steps,
  coming,
  faq,
  breadcrumbs,
  subnav,
  brand,
  cta,
  media,
}: TechnologyAudienceProps & { media?: ReactNode }) {
  return (
    <>
      <AtmosphericHero minHeight="46vh">
        <div className="mx-auto max-w-3xl text-center">
          <TechBreadcrumbs crumbs={breadcrumbs} />
          <p className="marketing-eyebrow-mono justify-center">{hero.eyebrow}</p>
          <h1 className="hero-headline mt-6" style={{ fontSize: 'clamp(2.1rem, 4.6vw, 3.6rem)' }}>
            {hero.title}
          </h1>
          <p className="marketing-lead mx-auto mt-7 max-w-[65ch]">{hero.body}</p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <a href={primary_cta.href} className="marketing-button-primary inline-flex">
              {primary_cta.label}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </a>
            {secondary_cta && (
              <a href={secondary_cta.href} className="marketing-button-secondary inline-flex">
                {secondary_cta.label}
              </a>
            )}
          </div>
        </div>
      </AtmosphericHero>

      <TechSubnav items={subnav} brand={brand} />

      <section aria-labelledby="what-you-get" className="container mx-auto px-6 py-20 lg:py-24">
        <div className="mx-auto max-w-3xl">
          <h2 id="what-you-get" className="marketing-section-title">
            What you get
          </h2>
          <p className="marketing-lead mt-5 max-w-[65ch]">{answer}</p>
        </div>

        {media && (
          <ScrollReveal>
            <div data-audience-visual="" className="mx-auto mt-14 max-w-5xl">
              {media}
            </div>
          </ScrollReveal>
        )}

        <ol className="mx-auto mt-16 flex max-w-3xl flex-col">
          {groups.map((group, index) => (
            <li key={group.key} id={group.key} className="scroll-mt-52 sm:scroll-mt-44 border-t border-white/[0.08] py-10 first:border-t-0 first:pt-0">
              <p className="font-mono text-[0.75rem] text-white/45" aria-hidden="true">
                {String(index + 1).padStart(2, '0')}
              </p>
              <h3
                className="mt-2 text-xl font-semibold text-white"
                style={{ fontFamily: 'var(--wavbee-font-display)' }}
              >
                {group.title}
              </h3>
              <p className="mt-3 max-w-[65ch] text-[0.975rem] leading-[1.8] text-white/65">{group.body}</p>

              {group.bullets.length > 0 && (
                <ul className="mt-5 flex max-w-[65ch] flex-col gap-2.5">
                  {group.bullets.map((bullet) => (
                    <li key={bullet} className="flex gap-3 text-[0.9375rem] leading-relaxed text-white/75">
                      <Check className="mt-1 h-4 w-4 shrink-0 text-[#ff8c4d]" aria-hidden="true" />
                      {bullet}
                    </li>
                  ))}
                </ul>
              )}

              {group.modules.length > 0 && (
                <ul aria-label={`${group.title}: capabilities`} className="mt-6 flex flex-wrap gap-2">
                  {group.modules.map((chip) => (
                    <li key={chip.key}>
                      <CapabilityChip chip={chip} />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
      </section>

      <section aria-labelledby="how-it-works" className="container mx-auto px-6 pb-20 lg:pb-24">
        <div className="mx-auto max-w-5xl">
          <h2 id="how-it-works" className="marketing-section-title">
            How it works
          </h2>
          <ol className={`mt-10 grid gap-8 sm:grid-cols-2 ${steps.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
            {steps.map((step, index) => (
              <li key={step.title} className="border-t border-white/[0.12] pt-5">
                <span
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-[#ff6a1a]/40 font-mono text-[0.8125rem] text-[#ff8c4d]"
                  aria-hidden="true"
                >
                  {index + 1}
                </span>
                <h3 className="mt-4 text-base font-semibold text-white">{step.title}</h3>
                <p className="mt-2 text-[0.9375rem] leading-relaxed text-white/65">{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {coming.length > 0 && (
        <section aria-labelledby="whats-coming" className="container mx-auto px-6 pb-8">
          <div className="mx-auto max-w-3xl rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6 sm:p-8">
            <h2 id="whats-coming" className="text-lg font-semibold text-white" style={{ fontFamily: 'var(--wavbee-font-display)' }}>
              What&rsquo;s coming
            </h2>
            <ul className="mt-5 flex flex-col gap-4">
              {coming.map((mod) => (
                <li key={mod.key}>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                    <span className="text-[0.9375rem] font-medium text-white/85">{mod.name}</span>
                    <StatusBadge status={mod.status} label={mod.status_label} />
                  </div>
                  {mod.summary && <p className="mt-1.5 max-w-[65ch] text-sm leading-relaxed text-white/60">{mod.summary}</p>}
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {faq.length > 0 && <FeatureFaq faq={faq} />}

      <TechCta cta={cta} />
    </>
  )
}

function CapabilityChip({ chip }: { chip: TechnologyModuleChip }) {
  const live = chip.status === 'live'
  return (
    <a
      href={chip.href}
      className="marketing-chip inline-flex items-center gap-2 no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--wavbee-orange)]"
    >
      {chip.name}
      {!live && (
        <span data-chip-status={chip.status}>
          <StatusBadge status={chip.status} label={chip.status_label} />
        </span>
      )}
    </a>
  )
}
